import { Injectable, signal } from '@angular/core';

export interface HelpArticle {
  title: string;
  body: string;
  tags?: string[];
}

export interface HelpContent {
  pageTitle: string;
  summary: string;
  articles: HelpArticle[];
  quickTips: string[];
}

export const HELP_CONTENT: Record<string, HelpContent> = {
  dashboard: {
    pageTitle: 'Dashboard Help',
    summary: 'The dashboard gives you a real-time overview of your entire operation across all sites.',
    articles: [
      { title: 'Understanding KPI Cards', body: 'The four cards at the top show Open Tasks, Active Sites, Open Incidents, and Audit Score. Hover over delta values to see week-over-week trends. Click a card to navigate to that module.' },
      { title: 'Quick Actions', body: 'Use the Quick Actions grid to jump to common workflows without navigating the sidebar. New Task, Log Incident, Start Audit, and Add Site are the most frequently used actions.' },
      { title: 'Activity Feed', body: 'The feed shows all team activity in real time — task assignments, incident reports, audit completions, and site updates. It refreshes automatically.' },
      { title: 'Charts & Analytics', body: 'The task breakdown donut and incident trend line give you visual insight into operational health. Green = on track, Red = attention needed.' },
    ],
    quickTips: [
      'Click any stat card to jump directly to that module',
      'The Activity Feed shows events from the last 30 days',
      'Your audit score is the average of all completed audits this month',
      'Use the greeting time to check if your session is still active',
    ]
  },
  tasks: {
    pageTitle: 'Tasks Help',
    summary: 'Tasks are the core of daily operations. Assign work, track progress, and ensure nothing falls through the cracks.',
    articles: [
      { title: 'Creating a Task', body: 'Click "New Task" and fill in the title, site, category, priority, assignee, and due date. The task immediately appears in the list and the assignee can see it in their queue.' },
      { title: 'Priority Levels', body: 'High priority tasks appear first and are marked in red. Medium tasks are yellow, Low are gray. Set priority based on safety impact and deadlines.' },
      { title: 'List vs Kanban View', body: 'List view shows all task details in a sortable table. Kanban view organizes tasks into columns: To Do, In Progress, Overdue, and Completed. Use Kanban for daily standups.' },
      { title: 'Statuses Explained', body: 'Pending = not started. In Progress = assigned and active. Overdue = past due date and incomplete. Completed = done and verified. Update status by clicking the row or using the status button in the detail panel.' },
    ],
    quickTips: [
      'Click any row to open the task detail panel on the right',
      'Use the stat chips at the top as quick status filters',
      'Mark tasks complete directly from the Kanban card\'s checkmark button',
      'The assignee avatar shows who owns the task',
    ]
  },
  sites: {
    pageTitle: 'Sites Help',
    summary: 'Sites represent your physical locations. Everything in SiteWareOS is connected to a site.',
    articles: [
      { title: 'Adding a New Site', body: 'Click "Add Site" and enter the site name, address, type (office, warehouse, etc.), manager, and contact information. The site then becomes available in all module dropdowns.' },
      { title: 'Site Health Indicators', body: 'Each site card shows Open Tasks, Open Incidents, and Last Audit Score. A score above 90% is excellent, 75-89% is good, below 75% needs attention.' },
      { title: 'Grid vs List View', body: 'Grid view gives you a visual overview of all sites. List view shows a detailed table with all fields. Use the view toggle buttons in the filter bar.' },
      { title: 'Site Status Types', body: 'Active = fully operational. Maintenance = undergoing work, reduced capacity. Inactive = not currently in use. Filter by status using the stat chips.' },
    ],
    quickTips: [
      'Click a site card to open the full site command center',
      'Edit a site using the pencil icon that appears on card hover',
      'Filter sites by status using the colored stat chips',
      'Sites with open critical incidents show a red incident count',
    ]
  },
  incidents: {
    pageTitle: 'Incidents Help',
    summary: 'Log, investigate, and resolve safety incidents. Every incident has a lifecycle from Open → Investigating → Resolved.',
    articles: [
      { title: 'Logging an Incident', body: 'Click "Log Incident" and fill in the title, site, severity (Critical/High/Medium/Low), category, and description. The incident is immediately logged with a timestamp and ID.' },
      { title: 'Severity Levels', body: 'Critical = immediate danger, stop work, notify management. High = significant risk, resolve within 24 hours. Medium = moderate risk, resolve within 48 hours. Low = minor issue, resolve within a week.' },
      { title: 'Incident Lifecycle', body: 'Open → Investigating → Resolved. Update the status from the detail drawer on the right. Add comments to document investigation progress.' },
      { title: 'Comment Thread', body: 'Click any incident row to open the detail drawer. Scroll to the bottom to see the comment thread. Type a comment and click Send to add an update.' },
    ],
    quickTips: [
      'Critical incidents always appear at the top of the list',
      'Add photos and evidence via the attach option in the log form',
      'Assign incidents to the most qualified team member for faster resolution',
      'Resolved incidents are kept for compliance records — don\'t delete them',
    ]
  },
  audits: {
    pageTitle: 'Audits Help',
    summary: 'Schedule compliance audits, conduct inspections, and track scores across all sites.',
    articles: [
      { title: 'Scheduling an Audit', body: 'Click "Schedule Audit" to create a new audit. Select the site, audit type, assigned auditor, date, and number of questions in the checklist.' },
      { title: 'Audit Types', body: 'Safety, Fire Safety, Environmental, Security, Equipment, Health, and Compliance are the built-in audit types. Each can have its own question template.' },
      { title: 'Starting an Audit', body: 'Scheduled audits can be started by clicking the "Start" button in the Actions column. This changes their status to In Progress.' },
      { title: 'Audit Scores', body: 'Completed audits have a score from 0-100. 90+ = Excellent (green), 75-89 = Good (yellow), below 75 = Poor (red). Track score trends to measure compliance improvement.' },
      { title: 'Template Builder', body: 'Use the Template Builder to create custom audit question sets for different site types or compliance requirements. Templates can be reused across audits.' },
    ],
    quickTips: [
      'Schedule audits at least 2 weeks in advance to notify auditors',
      'Attach the completed checklist PDF when finishing an audit',
      'Monthly security walkthroughs should be scheduled as recurring audits',
      'Export audit reports from the completed audit\'s action menu',
    ]
  },
  crm: {
    pageTitle: 'CRM Help',
    summary: 'Manage client relationships, track opportunities, and maintain your business development pipeline.',
    articles: [
      { title: 'Adding a Contact', body: 'Go to the Contacts tab and click "Add Contact". Enter the contact\'s name, company, role, email, and phone. Set their status to Active, Prospect, or Inactive.' },
      { title: 'Pipeline Kanban', body: 'The Pipeline tab shows opportunities in Kanban columns: Lead → Qualified → Proposal → Negotiation → Closed Won. Move opportunities using the arrow buttons on each card.' },
      { title: 'Adding Opportunities', body: 'Click "Add Opportunity" in the Pipeline tab. Set the deal title, company, estimated value, stage, close date, and probability percentage.' },
      { title: 'Contact Detail Panel', body: 'Click any contact row to open the detail panel on the right. See all contact information, tags, last contact date, and linked opportunities.' },
    ],
    quickTips: [
      'Use tags to segment contacts (e.g., "Priority", "Hot", "Prospect")',
      'The Companies tab shows all organizations with linked contacts',
      'Pipeline total value is shown at the bottom of each stage column',
      'Use the search bar to quickly find a contact by name, email, or company',
    ]
  },
  'work-orders': {
    pageTitle: 'Work Orders Help',
    summary: 'Create, assign, and track maintenance work orders. Manage vendor bids and track actual costs.',
    articles: [
      { title: 'Creating a Work Order', body: 'Click "New Work Order" and fill in the title, site, category (Plumbing, Electrical, etc.), priority, and due date. Optionally assign a vendor and estimated cost.' },
      { title: 'Pending Bid Status', body: 'Work orders without a vendor are in "Pending Bid" status. Use the Submit Bid button to assign a vendor and enter their quoted amount, changing the status to Open.' },
      { title: 'Work Order Statuses', body: 'Open = assigned and ready. In Progress = work started. Pending Bid = waiting for vendor quote. Completed = work done, actual cost recorded. Cancelled = work no longer needed.' },
      { title: 'Cost Tracking', body: 'Enter an Estimated Cost when creating the WO. When the work is complete, update the Actual Cost to track budget variance. Both appear in the table for comparison.' },
    ],
    quickTips: [
      'High priority WOs should be assigned to a vendor within 24 hours',
      'Use the detail panel to view full WO history and update status',
      'Completed WOs are kept for maintenance history — don\'t delete them',
      'Filter by "Pending Bid" to see WOs that need vendor assignment',
    ]
  },
  sop: {
    pageTitle: 'SOPs Help',
    summary: 'Create and maintain Standard Operating Procedures. Track compliance and ensure your team follows best practices.',
    articles: [
      { title: 'Creating an SOP', body: 'Click "New SOP" and enter the title, category (Safety, Maintenance, etc.), author, and a description of the procedure. Save as Draft, then Publish when ready.' },
      { title: 'SOP Status Lifecycle', body: 'Draft = being written, not visible to team. Review = submitted for approval. Active = published and in use. Update the status from the detail drawer.' },
      { title: 'Compliance Tracking', body: 'Each active SOP shows a compliance percentage — how many assigned staff have acknowledged and followed the procedure. Click the SOP row to see details.' },
      { title: 'Publishing an SOP', body: 'When an SOP is in Draft or Review status, click the "Publish" button in the Actions column or detail drawer to make it Active and visible to all users.' },
    ],
    quickTips: [
      'SOPs should be reviewed and updated annually at minimum',
      'Use categories to organize SOPs by department or function',
      'Active SOPs with <80% compliance need follow-up training',
      'Link SOPs to audit templates for automated compliance checks',
    ]
  },
  documents: {
    pageTitle: 'Documents Help',
    summary: 'Store, organize, and share operational documents. Supports PDFs, Word docs, spreadsheets, and presentations.',
    articles: [
      { title: 'Uploading Documents', body: 'Click "Upload Document" and enter the document name, destination folder, file type, and owner. In production, you\'d also attach the actual file.' },
      { title: 'Organizing with Folders', body: 'Click "+ New Folder" in the left sidebar to create a folder. Documents can be assigned to any folder. The "All Documents" folder shows everything.' },
      { title: 'Document Statuses', body: 'Signed = executed agreement. Active = current and in use. Pending Signature = awaiting sign-off. Documents without a status are general-purpose files.' },
    ],
    quickTips: [
      'Use the search bar to find documents by name across all folders',
      'Contracts and legal documents should be uploaded as signed PDFs',
      'Keep Insurance and Permit folders up to date for compliance',
      'Documents are accessible to all users with document permissions',
    ]
  },
  users: {
    pageTitle: 'Users Help',
    summary: 'Manage team members, roles, and access levels. Invite new users and control what they can see and do.',
    articles: [
      { title: 'Inviting a User', body: 'Click "Invite User" and enter their name, email address, role, and the number of sites they can access. They\'ll receive an email invitation to create their account.' },
      { title: 'User Roles Explained', body: 'Admin = full access. Manager = can create/edit everything. Inspector = can conduct audits. Technician = can view tasks and submit work. Viewer = read-only access.' },
      { title: 'Activating & Deactivating', body: 'Use the status toggle in the user row\'s Actions column to deactivate a user without deleting them. Deactivated users can\'t log in but their history is preserved.' },
      { title: 'Editing a User', body: 'Click the Edit button in the user\'s row to change their name, email, role, site count, or status. Changes take effect immediately.' },
    ],
    quickTips: [
      'Invited users appear with "Invited" status until they accept',
      'Use "Resend Invite" if a user didn\'t receive their invitation email',
      'Only Admins can invite new users or change roles',
      'Deactivate users who leave instead of deleting to preserve their history',
    ]
  },
  payments: {
    pageTitle: 'Payments Help',
    summary: 'Create invoices, track payments, and manage billing for all your client sites.',
    articles: [
      { title: 'Creating an Invoice', body: 'Click "Create Invoice" and select the client, site, invoice amount, issue date, and due date. The invoice is saved as Pending and appears in the invoice list.' },
      { title: 'Invoice Statuses', body: 'Pending = sent, awaiting payment. Overdue = past due date. Paid = payment received and confirmed. Update status using the "Mark as Paid" button.' },
      { title: 'Sending Reminders', body: 'For Pending and Overdue invoices, use the "Send Reminder" button (envelope icon) to notify the client. In production this sends an email automatically.' },
      { title: 'Financial Summary Cards', body: 'The four cards at the top show Total Paid, Pending amount, Overdue amount, and Total Revenue. Filter the table by clicking each status filter tab.' },
    ],
    quickTips: [
      'Invoices that are 30+ days overdue should escalate to management',
      'Use the detail panel (click any row) to see full invoice details',
      'Export invoices for accounting integration from the actions menu',
      'Send reminders 7 days before and 1 day after the due date',
    ]
  },
  marketing: {
    pageTitle: 'Marketing Help',
    summary: 'Manage email, SMS, and push campaigns with drip automation sequences.',
    articles: [
      { title: 'Creating a Campaign', body: 'Click "New Campaign" and enter the name, channel type (Email, SMS, or Push), start date, and number of drip steps. Campaigns start in Draft status.' },
      { title: 'Drip Steps', body: 'Drips are the number of automated follow-up messages in the sequence. For example, a 5-drip campaign sends an initial message plus 4 follow-ups at scheduled intervals.' },
      { title: 'Launching & Pausing', body: 'Use the play/pause button in the Actions column to launch a Draft campaign or pause an Active one. Completed campaigns cannot be restarted.' },
      { title: 'Campaign Metrics', body: 'Sent = total messages delivered. Open Rate = opens ÷ sent. CTR (Click-Through Rate) = clicks ÷ opens. Industry benchmarks: Email open 25%, CTR 3-5%.' },
    ],
    quickTips: [
      'Draft campaigns don\'t send anything — launch them when ready',
      'Paused campaigns can be resumed from where they left off',
      'A/B test campaigns by duplicating and varying the subject line',
      'Higher drip counts don\'t always mean better results — start with 3-5',
    ]
  },
};

@Injectable({ providedIn: 'root' })
export class HelpPanelService {
  readonly isOpen = signal(false);
  readonly currentPage = signal('dashboard');
  readonly searchQuery = signal('');

  open(page: string): void {
    this.currentPage.set(page);
    this.isOpen.set(true);
    this.searchQuery.set('');
  }

  close(): void {
    this.isOpen.set(false);
  }

  getContent(page: string): HelpContent | null {
    return HELP_CONTENT[page] ?? null;
  }
}
